// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import { useCallback, useEffect, useState } from "react";

import { LockKeyhole, RefreshCw } from "lucide-react";

import type {
  CockpitApi,
  ChronicleLearningInspection,
  PersonalCoachingResponse,
  PersonalCoachingView,
} from "./api.js";
import { displayState, stateTone } from "./presentation.js";

interface LearningState {
  readonly coaching: PersonalCoachingView | null;
  readonly learning: ChronicleLearningInspection | null;
}

const EMPTY_STATE: LearningState = {
  coaching: null,
  learning: null,
};

function dollars(micros: number): string {
  return `$${(micros / 1_000_000).toFixed(2)}`;
}

function futureConsentExpiry(): string {
  const expiry = new Date();
  expiry.setUTCDate(expiry.getUTCDate() + 90);
  return expiry.toISOString();
}

export function Learning({ api }: { readonly api: CockpitApi }) {
  const [state, setState] = useState<LearningState>(EMPTY_STATE);
  const [status, setStatus] = useState("Loading controlled learning…");
  const [promotionIds, setPromotionIds] = useState<
    Readonly<Record<string, string>>
  >({});
  const [corrections, setCorrections] = useState<
    Readonly<Record<string, string>>
  >({});
  const [policyDigest, setPolicyDigest] = useState("");
  const [guidance, setGuidance] = useState<PersonalCoachingResponse | null>(
    null,
  );

  const reload = useCallback(async () => {
    setStatus("Loading controlled learning…");
    try {
      const [learning, coaching] = await Promise.all([
        api.learning(),
        api.personalCoaching(),
      ]);
      setState({ coaching, learning });
      setCorrections(
        Object.fromEntries(
          coaching.memories.map((memory) => [memory.memoryId, memory.content]),
        ),
      );
      setStatus("");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Loading failed.");
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function perform(label: string, operation: () => Promise<unknown>) {
    setStatus(label);
    try {
      await operation();
      await reload();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : `${label} failed.`);
    }
  }

  const consent = state.coaching?.consent ?? null;
  const activeConsent =
    consent?.state === "active" && Date.parse(consent.expiresAt) > Date.now();
  const proposals = state.learning?.proposals ?? [];
  const activeRun = state.learning?.runs.find(
    (run) => run.runId === state.learning?.activeRunId,
  );
  const latestRun = activeRun ?? state.learning?.runs[0];

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Learning</h1>
          <p>
            What reviews teach the project has to be approved by a person and
            proven by replay before it changes anything. What they teach you
            personally stays yours.
          </p>
        </div>
        <div className="page-head-actions">
          <button
            className="button secondary"
            onClick={() => void reload()}
            type="button"
          >
            <RefreshCw size={16} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </header>

      {status.length === 0 ? null : (
        <p aria-live="polite" className="status-line">
          {status}
        </p>
      )}

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Repository learning extraction</h2>
            <p>
              Durable, budget-limited extraction from resolved review history.
              Progress and failures remain visible and can be resumed safely.
            </p>
          </div>
          <span
            className={`state-badge tone-${stateTone(latestRun?.state ?? "not-started")}`}
          >
            {displayState(latestRun?.state ?? "not-started")}
          </span>
        </div>
        <div className="panel-pad stack">
          <dl className="chronicle-definition-grid">
            <div>
              <dt>Eligible reviews</dt>
              <dd>{state.learning?.eligibleEpisodeCount ?? 0}</dd>
            </div>
            <div>
              <dt>Processed</dt>
              <dd>
                {latestRun === undefined
                  ? "—"
                  : `${String(latestRun.processedEpisodeCount)} / ${String(latestRun.eligibleEpisodes.length)}`}
              </dd>
            </div>
            <div>
              <dt>Cost</dt>
              <dd>
                {latestRun === undefined
                  ? "—"
                  : `${dollars(latestRun.spentMicros)} / ${dollars(latestRun.budgetMicros)}`}
              </dd>
            </div>
            <div>
              <dt>Observations</dt>
              <dd>{state.learning?.observationTotal ?? 0}</dd>
            </div>
            <div>
              <dt>Semantic retrieval</dt>
              <dd>
                {displayState(
                  state.learning?.semanticProjection.state ?? "not-built",
                )}
              </dd>
              <small>
                {state.learning?.semanticProjection.encoderRevision ??
                  "No encoder revision"}
                {state.learning?.semanticProjection.projectionRevision ===
                  null ||
                state.learning?.semanticProjection.projectionRevision ===
                  undefined
                  ? ""
                  : ` · projection ${String(state.learning.semanticProjection.projectionRevision)}`}
              </small>
            </div>
          </dl>
          {latestRun?.failure === null || latestRun === undefined ? null : (
            <div className="privacy-note">
              <span>
                {displayState(latestRun.failure.code)} on review{" "}
                {latestRun.failure.sourceRecordId}, attempt{" "}
                {String(latestRun.failure.attempt)}
                {latestRun.failure.retryAt === null
                  ? "."
                  : `. Retry after ${new Date(latestRun.failure.retryAt).toLocaleString()}.`}
              </span>
            </div>
          )}
          <div className="button-row">
            {activeRun === undefined ? (
              <button
                className="button secondary"
                disabled={
                  (state.learning?.eligibleEpisodeCount ?? 0) === 0 &&
                  state.learning?.semanticProjection.state === "current"
                }
                onClick={() =>
                  void perform("Starting learning extraction…", () => {
                    const mode =
                      (state.learning?.runTotal ?? 0) === 0
                        ? "historical"
                        : "incremental";
                    return api.startLearningRun({
                      budgetMicros: mode === "historical" ? 2_000_000 : 250_000,
                      mode,
                      runId: `learning-run:${crypto.randomUUID()}`,
                    });
                  })
                }
                type="button"
              >
                Start{" "}
                {state.learning?.runTotal === 0 ? "historical" : "incremental"}{" "}
                run
              </button>
            ) : (
              <button
                className="button danger-quiet"
                onClick={() =>
                  void perform("Cancelling learning extraction…", () =>
                    api.cancelLearningRun(activeRun.runId, activeRun.revision),
                  )
                }
                type="button"
              >
                Cancel run
              </button>
            )}
            <small>
              Historical runs are capped at $2.00; incremental runs at $0.25.
            </small>
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Extracted observations</h2>
            <p>
              Candidate knowledge with its review and evidence identities kept
              visible. Corroboration does not make it policy.
            </p>
          </div>
          <span className="count-badge">
            {state.learning?.observationTotal ?? 0}
          </span>
        </div>
        {(state.learning?.observations.length ?? 0) === 0 ? (
          <div className="empty-state inline">
            <strong>No observations extracted yet</strong>
            <span>
              A learning run will populate this list from eligible resolved
              reviews.
            </span>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Observation</th>
                  <th>Status</th>
                  <th>Confidence</th>
                  <th>Evidence</th>
                </tr>
              </thead>
              <tbody>
                {state.learning?.observations.map((observation) => (
                  <tr key={observation.observationId}>
                    <th>
                      {observation.statement}
                      <small>{observation.observationId}</small>
                    </th>
                    <td>{displayState(observation.status)}</td>
                    <td>{Math.round(observation.confidence * 100)}%</td>
                    <td>
                      {String(observation.evidenceAtomIds.length)} atoms
                      <small>
                        {String(observation.sourceReviewEpisodeIds.length)}{" "}
                        review
                        {observation.sourceReviewEpisodeIds.length === 1
                          ? ""
                          : "s"}
                      </small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Proposed improvements</h2>
            <p>
              Shared with the project. A pattern observed across reviews changes
              nothing until a maintainer approves it and replay evidence passes.
            </p>
          </div>
          <span className="count-badge">
            {state.learning?.proposalTotal ?? 0}
          </span>
        </div>
        {proposals.length === 0 ? (
          <div className="empty-state inline">
            <strong>Nothing is waiting for approval</strong>
            <span>
              Patterns that clear the evidence bar appear here, each with the
              reviews it came from and the authority needed to accept it.
            </span>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Proposal</th>
                  <th>Target</th>
                  <th>Source</th>
                  <th>State</th>
                  <th>Authority</th>
                </tr>
              </thead>
              <tbody>
                {proposals.map(({ evaluationTarget, proposal }) => (
                  <tr key={proposal.proposalId}>
                    <th>
                      {proposal.summary}
                      <small>{proposal.proposalId}</small>
                    </th>
                    <td>{proposal.target}</td>
                    <td>
                      {proposal.source.kind}
                      <small>{proposal.source.reference}</small>
                    </td>
                    <td>
                      <span
                        className={`state-badge tone-${stateTone(proposal.state)}`}
                      >
                        {displayState(proposal.state)}
                      </span>
                    </td>
                    <td>
                      {proposal.state === "proposed" ? (
                        <button
                          className="button secondary"
                          onClick={() =>
                            void perform("Approving proposal…", () =>
                              api.transitionLearningProposal(
                                proposal.proposalId,
                                {
                                  evaluationPromotionId: null,
                                  expectedRevision: proposal.revision,
                                  reason: "maintainer-reviewed",
                                  state: "approved",
                                },
                              ),
                            )
                          }
                          type="button"
                        >
                          Approve
                        </button>
                      ) : proposal.state === "approved" ||
                        proposal.state === "revised" ? (
                        <div className="inline-action">
                          <label>
                            Accepted Evaluation promotion ID
                            <input
                              onChange={(event) => {
                                setPromotionIds((current) => ({
                                  ...current,
                                  [proposal.proposalId]: event.target.value,
                                }));
                              }}
                              placeholder="promotion:learning:revision"
                              value={promotionIds[proposal.proposalId] ?? ""}
                            />
                            <span className="field-help">
                              Exact target {evaluationTarget.identity} revision{" "}
                              {evaluationTarget.revision}
                            </span>
                          </label>
                          <button
                            className="button primary"
                            disabled={
                              (promotionIds[proposal.proposalId] ?? "")
                                .length === 0
                            }
                            onClick={() =>
                              void perform("Activating proposal…", () =>
                                api.transitionLearningProposal(
                                  proposal.proposalId,
                                  {
                                    evaluationPromotionId:
                                      promotionIds[proposal.proposalId] ?? null,
                                    expectedRevision: proposal.revision,
                                    reason: "replay-passed",
                                    state: "active",
                                  },
                                ),
                              )
                            }
                            type="button"
                          >
                            Activate
                          </button>
                        </div>
                      ) : (
                        <span>Recorded</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>Your personal coaching</h2>
            <p>
              Optional reminders drawn from your own review habits. They never
              change shared policy and never appear in anyone else&rsquo;s view,
              including your manager&rsquo;s.
            </p>
          </div>
          <span className={`state-badge ${activeConsent ? "tone-active" : ""}`}>
            {activeConsent ? "On" : "Off"}
          </span>
        </div>
        <div className="panel-pad stack">
          <div className="privacy-note">
            <LockKeyhole size={17} aria-hidden="true" />
            <span>
              Held under your own identity, and excluded from shared Chronicle
              exports, evaluation, analytics, and every manager view.
            </span>
          </div>
          <div className="button-row">
            <button
              className={activeConsent ? "button danger" : "button primary"}
              onClick={() =>
                void perform(
                  activeConsent
                    ? "Revoking and purging coaching memory…"
                    : "Enabling private coaching…",
                  () =>
                    api.setPersonalCoachingConsent({
                      enabled: !activeConsent,
                      expectedRevision: consent?.revision ?? 0,
                      expiresAt: consent?.expiresAt ?? futureConsentExpiry(),
                      reason: activeConsent
                        ? "developer-revocation"
                        : "developer-opt-in",
                    }),
                )
              }
              type="button"
            >
              {activeConsent ? "Turn off and delete" : "Turn on for 90 days"}
            </button>
            {consent === null ? null : (
              <small>
                Expires {new Date(consent.expiresAt).toLocaleDateString()}
              </small>
            )}
          </div>

          {state.coaching?.memories.map((memory) => (
            <article className="coaching-memory" key={memory.memoryId}>
              <div className="coaching-memory-head">
                <span className="state-badge">{displayState(memory.kind)}</span>
                <small>
                  Kept until{" "}
                  {new Date(memory.retentionUntil).toLocaleDateString()}
                </small>
              </div>
              <label>
                Your correction
                <textarea
                  onChange={(event) => {
                    setCorrections((current) => ({
                      ...current,
                      [memory.memoryId]: event.target.value,
                    }));
                  }}
                  rows={3}
                  value={corrections[memory.memoryId] ?? memory.content}
                />
              </label>
              <div className="button-row">
                <button
                  className="button secondary"
                  disabled={
                    (corrections[memory.memoryId] ?? memory.content) ===
                    memory.content
                  }
                  onClick={() =>
                    void perform("Saving correction…", () =>
                      api.correctPersonalCoachingMemory(memory.memoryId, {
                        content: corrections[memory.memoryId] ?? memory.content,
                        expectedRevision: memory.revision,
                        reason: "developer-correction",
                      }),
                    )
                  }
                  type="button"
                >
                  Save correction
                </button>
                <button
                  className="button danger-quiet"
                  onClick={() =>
                    void perform("Deleting coaching memory…", () =>
                      api.deletePersonalCoachingMemory(
                        memory.memoryId,
                        memory.revision,
                      ),
                    )
                  }
                  type="button"
                >
                  Delete
                </button>
              </div>
            </article>
          ))}

          <div className="coaching-generator">
            <label>
              Policy lock digest
              <input
                aria-describedby="policy-digest-help"
                className="code-input"
                onChange={(event) => {
                  setPolicyDigest(event.target.value);
                }}
                pattern="[a-f0-9]{64}"
                placeholder="64-character SHA-256 digest"
                value={policyDigest}
              />
              <span className="field-help" id="policy-digest-help">
                A matching digest is the proof that personal guidance left
                shared review policy untouched.
              </span>
            </label>
            <button
              className="button primary"
              disabled={!activeConsent || !/^[a-f0-9]{64}$/u.test(policyDigest)}
              onClick={() =>
                void perform("Generating private coaching…", async () => {
                  setGuidance(await api.generatePersonalCoaching(policyDigest));
                })
              }
              type="button"
            >
              Generate private coaching
            </button>
          </div>
          {guidance === null ? null : (
            <div className="coaching-guidance">
              <h3>For your eyes only</h3>
              {guidance.guidance.length === 0 ? (
                <p>None of your saved reminders apply to this review.</p>
              ) : (
                <ul>
                  {guidance.guidance.map((entry) => (
                    <li key={entry.memoryId}>{entry.statement}</li>
                  ))}
                </ul>
              )}
              <small>Shared review policy was not changed.</small>
            </div>
          )}
        </div>
      </section>
    </section>
  );
}
